import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { sprintf } from 'sprintf-js';

import { Box } from 'components/Box';
import { Notifications } from 'components/Notifications';
import {
  PageHeaderDropdownMenu,
  PageHeaderDropdownMenuItem,
  PageHeaderDropdownMenuSection,
  PageHeaderDropdownMenuSectionItemRouter,
  PageHeaderDropdownMenuSectionTitle,
} from 'components/Page/Header/PageHeaderDropdownMenu';
import { MainStoreState } from 'modules/main/store';
import {
  fetchNotifications,
  markNotificationsAsRead,
} from 'modules/main/store/ducks/userNotifications';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { hasUsedAts } from 'modules/orgDashboard/utils/ats';
import { colors, transition } from 'theme';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { isBrowser } from 'utils/browser';
import {
  NOTIFICATIONS,
  dismissNotification as dismissLocalNotification,
  notificationIsDismissed,
} from 'utils/localNotifications';
import { hasResumeDb, promoteResumeDatabase } from 'utils/resumeDatabase';
import { promoteSalarySurvey } from 'utils/salarySurvey';

import { PageHeaderAccountDropdownBadge } from './PageHeaderAccountDropdownBadge';
import { PageHeaderAccountDropdownConditionalMenuItem } from './PageHeaderAccountDropdownConditionalMenuItem';
import { PageHeaderAccountDropdownProfileSection } from './PageHeaderAccountDropdownProfileSection';
import { PageHeaderAtsCtaNotification } from './PageHeaderAtsCtaNotification';

const NAV_WIDTH = {
  en: 900,
  es: 1050,
  pt: 1000,
};

type Props = {
  isStaff: boolean;
};

export function PageHeaderAccountDropdown({ isStaff }: Props) {
  const userEnvironment = useUserEnvironment();
  const { user } = userEnvironment;
  const dispatch = useMainDispatch();
  const { userNotifications } = useSelector((state: MainStoreState) => ({
    userNotifications: state.userNotifications,
  }));
  const [atsNotificationDismissed, setAtsNotificationDismissed] = useState(
    notificationIsDismissed(NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA),
  );
  const isAdmin = (user?.orgs?.length || 0) > 0;
  const orgUsedAts = (user?.orgs.filter((org) => org.usedAts)?.length || 0) > 0;
  const promoteResumeDb = promoteResumeDatabase({
    userEnvironment,
  });
  const showSalarySurveyLink = promoteSalarySurvey({
    userEnvironment,
  });
  const promoteAts =
    isAdmin && !atsNotificationDismissed && !hasUsedAts() && !orgUsedAts;
  const notificationCount =
    (userNotifications.notifications
      ? userNotifications.notifications.length
      : 0) +
    (promoteResumeDb ? 1 : 0) +
    (promoteAts ? 1 : 0);
  const showBadge = isBrowser() && notificationCount > 0;

  const dismissAtsNotification = () => {
    dismissLocalNotification(NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA);
    setAtsNotificationDismissed(true);
  };

  const dismissNotification = (ids: Array<string>): void => {
    dispatch(markNotificationsAsRead(ids));
  };

  useEffect(() => {
    if (user?.hasNotifications && !userNotifications.isLoaded) {
      dispatch(fetchNotifications());
    }
  }, [user?.hasNotifications, dispatch, userNotifications.isLoaded]);

  const openMenu = (isOpen: boolean) => {
    if (isOpen && showSalarySurveyLink) {
      trackEvent('Displayed: Salary Survey Ad', {
        context: 'User Menu',
      });
    }
  };

  return (
    <Box position="relative">
      <PageHeaderDropdownMenu
        aria-label={getText('Account Information')}
        onClick={openMenu}
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/no-unstable-nested-components
        title={() => (
          <Box
            display={['none', null, null, 'flex']}
            alignItems="center"
            maxWidth={[
              null,
              null,
              null,
              `calc(100vw - ${NAV_WIDTH[CURRENT_LOCALE]}px)`,
            ]}
            minWidth={[null, null, null, 32]}
          >
            <Box
              whiteSpace="noWrap"
              textOverflow="ellipsis"
              overflow="hidden"
              pl={showBadge ? 'calc(1em + 10px)' : undefined}
            >
              {
                /* TRANSLATORS: user greeting at top of page */
                sprintf(getText('Hi, %(name)s!'), {
                  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                  name: user.firstName || user.email,
                })
              }
            </Box>
          </Box>
        )}
        data-qa-id="nav-dropdown"
      >
        {({ closeMenu, open }) => (
          <>
            {promoteResumeDb && (
              <PageHeaderAccountDropdownProfileSection closeMenu={closeMenu} />
            )}
            {userNotifications.isLoaded && userNotifications.notifications && (
              <Notifications
                notifications={userNotifications.notifications}
                dismiss={dismissNotification}
                open={open}
              />
            )}
            {promoteAts && (
              <PageHeaderAtsCtaNotification dismiss={dismissAtsNotification} />
            )}
            {showSalarySurveyLink && (
              <PageHeaderAccountDropdownConditionalMenuItem
                to="/salaries"
                isStaff={isStaff}
                onClick={() => {
                  trackClicked('Clicked: Salary Survey Ad', {
                    cta_type: 'Link',
                    button_text: 'Take Our Salary Survey',
                    url: '/salaries',
                    context: 'User Menu',
                  });
                }}
              >
                {getText('Take Our Salary Survey')}
              </PageHeaderAccountDropdownConditionalMenuItem>
            )}
            <PageHeaderAccountDropdownConditionalMenuItem
              to="/dashboard/email-alerts"
              isStaff={isStaff}
              onClick={closeMenu}
            >
              {getText('Email Alerts')}
            </PageHeaderAccountDropdownConditionalMenuItem>
            {hasResumeDb(userEnvironment) && (
              <PageHeaderAccountDropdownConditionalMenuItem
                to="/dashboard/employment-profile"
                isStaff={isStaff}
                onClick={closeMenu}
              >
                {getText('Employment Profile')}
              </PageHeaderAccountDropdownConditionalMenuItem>
            )}
            <PageHeaderAccountDropdownConditionalMenuItem
              to="/dashboard/account-settings"
              isStaff={isStaff}
              onClick={closeMenu}
            >
              {getText('My Account')}
            </PageHeaderAccountDropdownConditionalMenuItem>

            {
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              user.orgs.length > 0 && (
                <PageHeaderDropdownMenuSection>
                  <PageHeaderDropdownMenuSectionTitle>
                    {getText('My Organizations')}
                  </PageHeaderDropdownMenuSectionTitle>
                  {
                    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                    user.orgs.map((org) =>
                      isStaff ? (
                        <PageHeaderDropdownMenuItem
                          key={org.id}
                          href={
                            org.hasGradProgram && !org.overrideToMainDashboard
                              ? `/admin/${org.id}/grad-program`
                              : `/admin/${org.id}`
                          }
                          onClick={closeMenu}
                        >
                          {org.name}
                        </PageHeaderDropdownMenuItem>
                      ) : (
                        <PageHeaderDropdownMenuSectionItemRouter
                          key={org.id}
                          to={
                            org.hasGradProgram && !org.overrideToMainDashboard
                              ? `/admin/${org.id}/grad-program`
                              : `/admin/${org.id}`
                          }
                          onClick={closeMenu}
                        >
                          {org.name}
                        </PageHeaderDropdownMenuSectionItemRouter>
                      ),
                    )
                  }
                </PageHeaderDropdownMenuSection>
              )
            }
            <PageHeaderDropdownMenuItem href="/logout" data-qa-id="logout-link">
              {getText('Log Out')}
            </PageHeaderDropdownMenuItem>
          </>
        )}
      </PageHeaderDropdownMenu>

      {showBadge && (
        <Box
          position="absolute"
          zIndex={2000}
          left={12}
          top={11}
          pointerEvents="none"
        >
          <PageHeaderAccountDropdownBadge
            data-qa-id="notification-count"
            bg={colors.decorativePoppy}
            transition={`all ${transition}`}
            count={notificationCount}
          />
        </Box>
      )}
    </Box>
  );
}
